import {FC, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {Add} from '@mui/icons-material';
import {AiImageGroup} from '../../types/aiImage';
import {useCreateAiImageGroup} from '../../hooks/useAiImages';

interface Props {
    groups: AiImageGroup[];
    isLoading: boolean;
    selectedGroupId?: number;
    onSelect: (groupId: number) => void;
}

export const AiImageGroupList: FC<Props> = ({groups, isLoading, selectedGroupId, onSelect}) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [name, setName] = useState('');
    const [systemContext, setSystemContext] = useState('');
    const [description, setDescription] = useState('');
    const [submitError, setSubmitError] = useState<string | null>(null);

    const createMut = useCreateAiImageGroup();

    const resetForm = () => {
        setName('');
        setSystemContext('');
        setDescription('');
        setSubmitError(null);
    };

    const handleOpen = () => {
        resetForm();
        setDialogOpen(true);
    };

    const handleClose = () => {
        if (createMut.isPending) return;
        setDialogOpen(false);
    };

    const handleSubmit = async () => {
        if (!name.trim() || !systemContext.trim()) {
            setSubmitError('그룹명과 시스템 컨텍스트는 필수입니다.');
            return;
        }
        try {
            const created = await createMut.mutateAsync({
                name: name.trim(),
                systemContext: systemContext.trim(),
                description: description.trim() || undefined,
            });
            setDialogOpen(false);
            resetForm();
            onSelect(created.id);
        } catch (e) {
            const message = e instanceof Error ? e.message : '그룹 생성 실패';
            setSubmitError(message);
        }
    };

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5}}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle1" fontWeight={600}>
                    그룹 목록 ({groups.length})
                </Typography>
                <Button
                    size="small"
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleOpen}
                >
                    새 그룹
                </Button>
            </Stack>

            {isLoading ? (
                <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
                    <CircularProgress size={24} />
                </Box>
            ) : groups.length === 0 ? (
                <Alert severity="info">
                    아직 그룹이 없습니다. 새 그룹을 만들어 시스템 컨텍스트를 등록하세요.
                </Alert>
            ) : (
                <Stack spacing={1}>
                    {groups.map((g) => {
                        const selected = g.id === selectedGroupId;
                        return (
                            <Card
                                key={g.id}
                                variant="outlined"
                                sx={{
                                    borderColor: selected ? 'primary.main' : 'divider',
                                    bgcolor: selected ? 'action.selected' : 'background.paper',
                                }}
                            >
                                <CardActionArea onClick={() => onSelect(g.id)}>
                                    <CardContent sx={{py: 1.5}}>
                                        <Typography
                                            variant="subtitle2"
                                            fontWeight={selected ? 700 : 600}
                                            noWrap
                                        >
                                            {g.name}
                                        </Typography>
                                        {g.description && (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                {g.description}
                                            </Typography>
                                        )}
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{display: 'block', mt: 0.5}}
                                        >
                                            #{g.id}
                                        </Typography>
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        );
                    })}
                </Stack>
            )}

            <Dialog open={dialogOpen} onClose={handleClose} fullWidth maxWidth="sm">
                <DialogTitle>새 AI 이미지 그룹</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{mt: 1}}>
                        <TextField
                            label="그룹명"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            fullWidth
                            required
                        />
                        <TextField
                            label="시스템 컨텍스트"
                            value={systemContext}
                            onChange={(e) => setSystemContext(e.target.value)}
                            fullWidth
                            required
                            multiline
                            rows={6}
                            helperText="최종 프롬프트 앞에 항상 붙는 공통 지시문 (예: 스타일/톤/제약)"
                        />
                        <TextField
                            label="설명 (선택)"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            fullWidth
                            multiline
                            rows={2}
                        />
                        {submitError && <Alert severity="error">{submitError}</Alert>}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={createMut.isPending}>
                        취소
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={createMut.isPending}
                    >
                        {createMut.isPending ? '생성 중...' : '생성'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
